import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// { [episodeId]: progressPercent (0-100) }
const WatchProgressContext = createContext({});

export function WatchProgressProvider({ children }) {
  const [progressMap, setProgressMap] = useState({});
  const { accessToken, isAuthenticated } = useAuth();

  // Keep a ref to the latest token so the stable `refresh` callback always
  // reads the current value without needing accessToken in its deps array.
  // Without this, any token refresh would produce a new `refresh` reference
  // → the useEffect would re-fire → duplicate GET /user/watch-progress.
  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  const refresh = useCallback(async () => {
    const token = accessTokenRef.current;
    if (!isAuthenticated || !token) return;
    try {
      const res = await axios.get(`${API}/user/watch-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProgressMap(res.data || {});
    } catch {
      // silently ignore — checkmarks just won't appear
    }
  }, [isAuthenticated]); // accessToken intentionally omitted — read via ref

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <WatchProgressContext.Provider value={progressMap}>
      {children}
    </WatchProgressContext.Provider>
  );
}

/** Returns progress percent (0-100) for a given episode ID. */
export function useWatchProgress(episodeId) {
  const map = useContext(WatchProgressContext);
  return map[episodeId] || 0;
}
